import { useEffect, useMemo, useState } from 'react';
import Widget from '../../../components/Dashboard/Widget';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import { CaseEventDTO } from 'api/model';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import { useQueryClient } from '@tanstack/react-query';
import { useCaseMarkEventsAsReadByAdmin } from 'api/case/case';
import {
  getTransportLogisticsGetUnhandledFaultyCarsQueryKey,
  useTransportLogisticsGetUnhandledFaultyCars,
} from 'api/transport-logistics/transport-logistics';
import { formatDateTime } from 'utils/date-helpers';
import CaseOpenLink from 'components/CaseOpenLink';
import Checkbox from 'components/inputs/Checkbox';
import useUsers from 'contexts/basicData/useUsers';
import {
  Centered,
  getTableRowClassName,
  SavingRowAnimationWrapper,
} from 'components/Dashboard/table-helpers';
import { LongTitles, TLWidgetsEnum, UPDATE_INTERVAL } from '../utils';
import useBaseCaseEventWidgetColumnSettings from './useBaseCaseEventWidgetColumnSettings';

const TABLE_NAME = 'faulty-car';

const FaultyCarWidget = () => {
  const [unhandledCars, setUnhandledCars] = useState<CaseEventDTO[]>([]);
  const [ongoingSaves, setOngoingSaves] = useState<number[]>([]);
  const users = useUsers();

  const {
    dashboardState: { daterange, selectedBusinessUnits, searchText },
  } = useDashboardControls();

  const queryClient = useQueryClient();
  const { mutateAsync: markEventAsRead, status: markAsReadStatus } =
    useCaseMarkEventsAsReadByAdmin({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries([
            `${getTransportLogisticsGetUnhandledFaultyCarsQueryKey()}`,
          ]);
        },
      },
    });

  const getFaultyCars = useTransportLogisticsGetUnhandledFaultyCars(
    {
      startDate: daterange[0] ?? undefined,
      endDate: daterange[1] ?? undefined,
      businessUnitIDs: selectedBusinessUnits,
    },
    {
      query: {
        refetchInterval: UPDATE_INTERVAL,
      },
    }
  );

  useEffect(() => {
    if (getFaultyCars.data) {
      setUnhandledCars(getFaultyCars.data);
    } else {
      setUnhandledCars([] as CaseEventDTO[]);
    }
  }, [getFaultyCars.data]);

  const filteredFaultyCars = useMemo(() => {
    if (!searchText.length) {
      return unhandledCars;
    }
    return unhandledCars
      ? unhandledCars.filter((car) => {
          return (
            car.comment?.toLowerCase().includes(searchText.toLowerCase()) ||
            car.registrationNumber
              .toLowerCase()
              .includes(searchText.toLowerCase()) ||
            (users[car.createdByID]?.name ?? '')
              .toLowerCase()
              .includes(searchText.toLowerCase())
          );
        })
      : [];
  }, [searchText, unhandledCars, users]);

  const baseColumnSettings = useBaseCaseEventWidgetColumnSettings();

  const columnSettings: ColumnSetting<CaseEventDTO, any>[] = useMemo(() => {
    const columns: ColumnSetting<CaseEventDTO, any>[] =
      baseColumnSettings.concat([
        {
          head: 'Tid',
          render: (row) => `${formatDateTime(row.created)}`,
          width: 100,
          sortFunction: (a, b) => a.created.getTime() - b.created.getTime(),
        },
        {
          head: 'Kommentar',
          render: (row) => row.comment,
          width: 300,
          sortFunction: (a, b) => a.comment.localeCompare(b.comment),
        },
        {
          head: ' ',
          render: (row) => (
            <Centered>
              <CaseOpenLink caseID={row.caseID} />
            </Centered>
          ),
          width: 20,
        },
        {
          head: 'Klart',
          render: (row) => (
            <Centered>
              <Checkbox
                disabled={markAsReadStatus === 'loading'}
                onChange={async () => {
                  setOngoingSaves([...ongoingSaves, row.id]);
                  await markEventAsRead({ data: [row.id] });
                  setOngoingSaves(ongoingSaves.filter((id) => id !== row.id));
                }}
              />
            </Centered>
          ),
          width: 30,
        },
      ]);
    return columns;
  }, [baseColumnSettings, markAsReadStatus, markEventAsRead, ongoingSaves]);

  return (
    <Widget
      title={LongTitles[TLWidgetsEnum.FaultyCar]}
      count={unhandledCars.length}
      isLoading={getFaultyCars.isLoading}
      info="Fälttestaren har vid Hemleverans rapporterat fel på bil eller inte godkänt inspektionen."
    >
      <SavingRowAnimationWrapper
        tableName={TABLE_NAME}
        savingIds={ongoingSaves}
      >
        <Table
          columnSettings={columnSettings}
          rows={filteredFaultyCars}
          rowClassName={(row) => getTableRowClassName(TABLE_NAME, row.id)}
          useColumnWidthAsFlexAmount
        />
      </SavingRowAnimationWrapper>
    </Widget>
  );
};

export default FaultyCarWidget;
